// src/pages/mvz/MvzVerificacionPage.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  mvzAdminApi,
  MvzAdminDetail,
  MvzAdminListItem,
} from "../../api/mvzAdminApi";

/* ─── Estado local ─────────────────────────────────────────── */
type FilterMode = "todos" | "pendientes";

interface ModalState {
  mvzId: string;
  nombre: string;
  /** null = todavía no se eligió */
  accion: "aprobar" | "rechazar" | null;
  motivo: string;
  saving: boolean;
}

/* ─── Visor de documentos ─────────────────────────────────── */
type DocType = "image" | "pdf" | "office" | "unknown";

interface DocViewerState {
  url: string;
  nombre: string;
}

/**
 * Detecta el tipo de archivo por la extensión de la URL o por el nombre.
 * Soporta: imágenes (jpg, png, gif, webp, svg), PDF, Office (doc, docx, xls, xlsx, ppt, pptx).
 */
function detectDocType(url: string): DocType {
  const clean = url.split("?")[0].toLowerCase();
  const ext = clean.split(".").pop() ?? "";
  if (
    [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "svg",
      "bmp",
      "tiff",
      "heic",
    ].includes(ext)
  )
    return "image";
  if (ext === "pdf") return "pdf";
  if (
    ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp"].includes(
      ext,
    )
  )
    return "office";
  // Fallback: intentar mostrar como imagen (S3 URLs con UUID sin extensión)
  return "unknown";
}

/**
 * URL para previsualizar el documento:
 *  - Imágenes        → la URL directa de S3
 *  - PDF             → embed directo (el navegador lo renderiza)
 *  - Office / otros  → Google Docs Viewer (no requiere plugins)
 */
function buildViewerUrl(url: string, type: DocType): string {
  if (type === "image") return url;
  if (type === "pdf") return url; // el browser renderiza PDFs nativamente
  // Google Docs Viewer para Office y archivos desconocidos
  return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
}

/* ─── Helpers ──────────────────────────────────────────────── */
function VerificadoBadge({ v }: { v: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: v ? "rgba(12,187,29,0.12)" : "rgba(249,129,21,0.12)",
        color: v ? "#0cbb1d" : "#f98115",
      }}
    >
      <i className={`bi ${v ? "bi-patch-check-fill" : "bi-hourglass-split"}`} />
      {v ? "Verificado" : "Pendiente"}
    </span>
  );
}

/* ─── Componente ───────────────────────────────────────────── */
export default function MvzVerificacionPage() {
  const [items, setItems] = useState<MvzAdminListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<FilterMode>("pendientes");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  /* Detail drawer */
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MvzAdminDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  /* Verification modal */
  const [modal, setModal] = useState<ModalState | null>(null);

  /* Visor de documentos */
  const [docViewer, setDocViewer] = useState<DocViewerState | null>(null);

  const PAGE_SIZE = 20;

  /* ── Abrir visor de documento ── */
  const openDoc = useCallback((url: string, nombre = "Documento") => {
    setDocViewer({ url, nombre });
  }, []);

  /* ── Fetch list ── */
  const fetchList = useCallback(async (p: number, f: FilterMode) => {
    setLoading(true);
    setError(null);
    try {
      const res = await mvzAdminApi.listar(p, PAGE_SIZE, f === "pendientes");
      const data = res.data?.content;
      setItems(data?.content ?? data?.items ?? []);
      setTotal(data?.totalElements ?? 0);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(page, filter);
  }, [page, filter, fetchList]);

  /* ── Fetch detail ── */
  const openDetail = useCallback(async (id: string) => {
    setDetailId(id);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const res = await mvzAdminApi.detalle(id);
      setDetail(res.data?.content ?? null);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  /* ── Submit verification ── */
  const handleVerificar = useCallback(async () => {
    if (!modal) return;
    if (!modal.accion) return;

    if (modal.accion === "rechazar" && !modal.motivo.trim()) {
      alert("Debes indicar el motivo del rechazo.");
      return;
    }

    setModal((m) => (m ? { ...m, saving: true } : m));

    try {
      await mvzAdminApi.verificar(modal.mvzId, {
        aprobado: modal.accion === "aprobar",
        motivo: modal.motivo.trim() || undefined,
      });

      const msg =
        modal.accion === "aprobar"
          ? `✅ MVZ "${modal.nombre}" verificado y aprobado.`
          : `❌ MVZ "${modal.nombre}" rechazado. Se notificó al médico.`;

      setToast(msg);
      setTimeout(() => setToast(null), 4000);
      setModal(null);
      // Refresh detail and list
      if (detailId) openDetail(detailId);
      fetchList(page, filter);
    } catch (e: any) {
      alert(e?.response?.data?.message ?? "Error al verificar MVZ");
      setModal((m) => (m ? { ...m, saving: false } : m));
    }
  }, [modal, detailId, openDetail, fetchList, page, filter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* ─── RENDER ─── */
  return (
    <div style={{ padding: "24px 0" }}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: "#1a7a2e",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            fontSize: "0.9rem",
            fontWeight: 600,
          }}
        >
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 className="page-title" style={{ marginBottom: 4 }}>
          <i
            className="bi bi-patch-check me-2"
            style={{ color: "var(--fg-green)" }}
          />
          Verificación Médicos Veterinarios Zootecnistas
        </h2>
        <p className="page-subtitle">
          Revisa el perfil completo, matrícula profesional y documento de
          graduación antes de aprobar o rechazar.
        </p>
      </div>

      {/* Filter bar */}
      <div
        style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}
      >
        {(["pendientes", "todos"] as FilterMode[]).map((f) => (
          <button
            key={f}
            onClick={() => {
              setFilter(f);
              setPage(0);
            }}
            style={{
              padding: "6px 16px",
              borderRadius: 20,
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.82rem",
              background: filter === f ? "var(--fg-green)" : "var(--fg-white)",
              color: filter === f ? "#fff" : "var(--fg-text)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            {f === "pendientes" ? "⏳ Solo pendientes" : "📋 Todos los MVZ"}
          </button>
        ))}
        <span
          style={{
            marginLeft: "auto",
            color: "var(--fg-muted)",
            fontSize: "0.82rem",
            alignSelf: "center",
          }}
        >
          {total} resultado(s)
        </span>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            background: "rgba(214,13,76,0.08)",
            color: "var(--fg-red)",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <i className="bi bi-exclamation-circle me-2" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="fg-card" style={{ overflow: "hidden", padding: 0 }}>
        <div style={{ overflowX: "auto" }}>
          <table className="table table-hover mb-0" style={{ minWidth: 700 }}>
            <thead style={{ background: "var(--fg-bg)", fontSize: "0.78rem" }}>
              <tr>
                <th
                  style={{
                    padding: "10px 16px",
                    fontWeight: 700,
                    color: "var(--fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  MVZ
                </th>
                <th
                  style={{
                    padding: "10px 16px",
                    fontWeight: 700,
                    color: "var(--fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Matrícula
                </th>
                <th
                  style={{
                    padding: "10px 16px",
                    fontWeight: 700,
                    color: "var(--fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Ciudad
                </th>
                <th
                  style={{
                    padding: "10px 16px",
                    fontWeight: 700,
                    color: "var(--fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Estado
                </th>
                <th
                  style={{
                    padding: "10px 16px",
                    fontWeight: 700,
                    color: "var(--fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Doc.
                </th>
                <th
                  style={{
                    padding: "10px 16px",
                    fontWeight: 700,
                    color: "var(--fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 40,
                      color: "var(--fg-muted)",
                    }}
                  >
                    <div className="spinner-border spinner-border-sm text-success me-2" />
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 40,
                      color: "var(--fg-muted)",
                    }}
                  >
                    <i
                      className="bi bi-inbox"
                      style={{
                        fontSize: "2rem",
                        display: "block",
                        marginBottom: 8,
                      }}
                    />
                    No hay MVZ{" "}
                    {filter === "pendientes"
                      ? "pendientes de verificación"
                      : "registrados"}
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((item) => (
                  <tr key={item.id} style={{ fontSize: "0.875rem" }}>
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        {item.fotoUrl ? (
                          <img
                            src={item.fotoUrl}
                            alt={item.nombre}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid var(--fg-green)",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: "var(--fg-green-soft)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "var(--fg-green)",
                              fontWeight: 700,
                              fontSize: "0.9rem",
                            }}
                          >
                            {(item.nombre || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div
                            style={{ fontWeight: 600, color: "var(--fg-text)" }}
                          >
                            {item.nombre}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--fg-muted)",
                            }}
                          >
                            {item.usuarioEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        verticalAlign: "middle",
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                      }}
                    >
                      {item.numeroMatricula}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        verticalAlign: "middle",
                        color: "var(--fg-muted)",
                        fontSize: "0.82rem",
                      }}
                    >
                      {[item.ciudadNombre, item.departamentoNombre]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </td>
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      <VerificadoBadge v={item.verificado} />
                    </td>
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      {item.documentoGraduacionUrl ? (
                        <button
                          onClick={() =>
                            openDoc(item.documentoGraduacionUrl!, item.nombre)
                          }
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--fg-blue)",
                            fontSize: "0.82rem",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: 0,
                            textDecoration: "underline",
                          }}
                        >
                          <i className="bi bi-eye" />
                          Ver doc.
                        </button>
                      ) : (
                        <span
                          style={{
                            color: "var(--fg-muted)",
                            fontSize: "0.78rem",
                          }}
                        >
                          Sin doc.
                        </span>
                      )}
                    </td>
                    <td
                      style={{ padding: "12px 16px", verticalAlign: "middle" }}
                    >
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          onClick={() => openDetail(item.id)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: "1px solid var(--fg-green)",
                            background: "transparent",
                            color: "var(--fg-green)",
                            cursor: "pointer",
                            fontSize: "0.78rem",
                          }}
                        >
                          <i className="bi bi-eye me-1" />
                          Revisar
                        </button>
                        {!item.verificado && (
                          <button
                            onClick={() =>
                              setModal({
                                mvzId: item.id,
                                nombre: item.nombre,
                                accion: null,
                                motivo: "",
                                saving: false,
                              })
                            }
                            style={{
                              padding: "4px 10px",
                              borderRadius: 6,
                              border: "none",
                              background: "var(--fg-green)",
                              color: "#fff",
                              cursor: "pointer",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                            }}
                          >
                            <i className="bi bi-shield-check me-1" />
                            Verificar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 8,
              padding: "12px 16px",
              borderTop: "1px solid var(--fg-bg)",
            }}
          >
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              style={{
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #ddd",
                background: page === 0 ? "#f5f5f5" : "#fff",
                cursor: page === 0 ? "default" : "pointer",
              }}
            >
              ‹ Anterior
            </button>
            <span style={{ fontSize: "0.82rem", color: "var(--fg-muted)" }}>
              Pág. {page + 1} / {totalPages}
            </span>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              style={{
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #ddd",
                background: page >= totalPages - 1 ? "#f5f5f5" : "#fff",
                cursor: page >= totalPages - 1 ? "default" : "pointer",
              }}
            >
              Siguiente ›
            </button>
          </div>
        )}
      </div>

      {/* ─── DETAIL DRAWER ─── */}
      {detailId && (
        <>
          <div
            onClick={() => setDetailId(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: 480,
              maxWidth: "95vw",
              background: "#fff",
              zIndex: 1050,
              overflowY: "auto",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Drawer header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h5
                style={{ margin: 0, fontWeight: 700, color: "var(--fg-text)" }}
              >
                <i
                  className="bi bi-person-badge me-2"
                  style={{ color: "var(--fg-green)" }}
                />
                Perfil MVZ
              </h5>
              <button
                onClick={() => setDetailId(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: "var(--fg-muted)",
                }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            {/* Drawer body */}
            <div style={{ padding: "24px", flex: 1 }}>
              {loadingDetail && (
                <div
                  style={{
                    textAlign: "center",
                    padding: 60,
                    color: "var(--fg-muted)",
                  }}
                >
                  <div className="spinner-border text-success" />
                  <p style={{ marginTop: 12 }}>Cargando perfil…</p>
                </div>
              )}
              {!loadingDetail && detail && (
                <>
                  {/* Foto + nombre */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      marginBottom: 24,
                    }}
                  >
                    {detail.fotoUrl ? (
                      <img
                        src={detail.fotoUrl}
                        alt={detail.nombre}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "3px solid var(--fg-green)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: "var(--fg-green-soft)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--fg-green)",
                          fontWeight: 800,
                          fontSize: "1.8rem",
                        }}
                      >
                        {(detail.nombre || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h4
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          color: "var(--fg-text)",
                        }}
                      >
                        {detail.nombre}
                      </h4>
                      <VerificadoBadge v={detail.verificado} />
                    </div>
                  </div>

                  {/* Info grid */}
                  {[
                    {
                      label: "Matrícula profesional",
                      value: detail.numeroMatricula,
                      icon: "bi-card-text",
                      highlight: true,
                    },
                    {
                      label: "Experiencia",
                      value: detail.experienciaAnios
                        ? `${detail.experienciaAnios} años`
                        : "—",
                      icon: "bi-award",
                    },
                    {
                      label: "Ciudad",
                      value:
                        [detail.ciudadNombre, detail.departamentoNombre]
                          .filter(Boolean)
                          .join(", ") || "—",
                      icon: "bi-geo-alt",
                    },
                    {
                      label: "Animales que atiende",
                      value:
                        detail.tiposAnimales?.map((a) => a.nombre).join(", ") ||
                        "—",
                      icon: "bi-heart",
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: "10px 0",
                        borderBottom: "1px solid #f5f5f5",
                        alignItems: "flex-start",
                      }}
                    >
                      <i
                        className={`bi ${row.icon}`}
                        style={{
                          color: "var(--fg-green)",
                          marginTop: 2,
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "var(--fg-muted)",
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          {row.label}
                        </div>
                        <div
                          style={{
                            fontWeight: row.highlight ? 700 : 500,
                            color: "var(--fg-text)",
                            fontFamily: row.highlight ? "monospace" : "inherit",
                            fontSize: row.highlight ? "1rem" : "0.9rem",
                            background: row.highlight
                              ? "var(--fg-green-soft)"
                              : "transparent",
                            padding: row.highlight ? "2px 8px" : 0,
                            borderRadius: row.highlight ? 4 : 0,
                          }}
                        >
                          {row.value}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Descripción */}
                  {detail.descripcion && (
                    <div
                      style={{
                        margin: "16px 0",
                        padding: 14,
                        background: "#f9f9f9",
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.72rem",
                          color: "var(--fg-muted)",
                          fontWeight: 600,
                          marginBottom: 6,
                        }}
                      >
                        EXPERIENCIA / DESCRIPCIÓN
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.88rem",
                          color: "var(--fg-text)",
                          lineHeight: 1.6,
                        }}
                      >
                        {detail.descripcion}
                      </p>
                    </div>
                  )}

                  {/* Servicios */}
                  {detail.servicios && detail.servicios.length > 0 && (
                    <div style={{ margin: "16px 0" }}>
                      <div
                        style={{
                          fontSize: "0.72rem",
                          color: "var(--fg-muted)",
                          fontWeight: 600,
                          marginBottom: 8,
                        }}
                      >
                        SERVICIOS OFRECIDOS
                      </div>
                      {detail.servicios.map((sv) => (
                        <span
                          key={sv.id}
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            margin: "3px 4px 3px 0",
                            background: "var(--fg-green-soft)",
                            color: "var(--fg-green)",
                            borderRadius: 20,
                            fontSize: "0.78rem",
                            fontWeight: 600,
                          }}
                        >
                          {sv.servicioNombre}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Documento de graduación */}
                  <div
                    style={{
                      margin: "20px 0",
                      padding: 16,
                      border: "2px dashed var(--fg-blue)",
                      borderRadius: 10,
                      background: "rgba(59,130,246,0.04)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--fg-muted)",
                        fontWeight: 600,
                        marginBottom: 8,
                      }}
                    >
                      📄 DOCUMENTO DE GRADUACIÓN
                    </div>
                    {detail.documentoGraduacionUrl ? (
                      <div
                        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                      >
                        <button
                          onClick={() =>
                            openDoc(
                              detail.documentoGraduacionUrl!,
                              detail.nombre,
                            )
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 16px",
                            background: "var(--fg-blue)",
                            color: "#fff",
                            borderRadius: 8,
                            border: "none",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                          }}
                        >
                          <i className="bi bi-eye" />
                          Ver documento
                        </button>
                        <a
                          href={detail.documentoGraduacionUrl}
                          download
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 14px",
                            background: "transparent",
                            color: "var(--fg-blue)",
                            borderRadius: 8,
                            border: "1px solid var(--fg-blue)",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            textDecoration: "none",
                          }}
                        >
                          <i className="bi bi-download" />
                          Descargar
                        </a>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "var(--fg-red)",
                        }}
                      >
                        <i className="bi bi-exclamation-triangle" />
                        <span style={{ fontSize: "0.85rem" }}>
                          No se subió documento de graduación
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Motivo de rechazo previo */}
                  {detail.motivoRechazoVerificacion && (
                    <div
                      style={{
                        margin: "16px 0",
                        padding: 14,
                        background: "rgba(214,13,76,0.06)",
                        borderLeft: "3px solid var(--fg-red)",
                        borderRadius: 4,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.72rem",
                          color: "var(--fg-muted)",
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        MOTIVO DE RECHAZO ANTERIOR
                      </div>
                      <p
                        style={{
                          margin: 0,
                          color: "var(--fg-red)",
                          fontSize: "0.88rem",
                        }}
                      >
                        {detail.motivoRechazoVerificacion}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Drawer footer — action buttons */}
            {!loadingDetail && detail && !detail.verificado && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  gap: 10,
                  background: "#fafafa",
                }}
              >
                <button
                  onClick={() =>
                    setModal({
                      mvzId: detail.id,
                      nombre: detail.nombre,
                      accion: "aprobar",
                      motivo: "",
                      saving: false,
                    })
                  }
                  style={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: 8,
                    border: "none",
                    background: "var(--fg-green)",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "0.9rem",
                  }}
                >
                  <i className="bi bi-check-circle me-2" />
                  Aprobar
                </button>
                <button
                  onClick={() =>
                    setModal({
                      mvzId: detail.id,
                      nombre: detail.nombre,
                      accion: "rechazar",
                      motivo: "",
                      saving: false,
                    })
                  }
                  style={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: 8,
                    border: "none",
                    background: "var(--fg-red)",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: "0.9rem",
                  }}
                >
                  <i className="bi bi-x-circle me-2" />
                  Rechazar
                </button>
              </div>
            )}
            {!loadingDetail && detail && detail.verificado && (
              <div
                style={{
                  padding: "16px 24px",
                  background: "rgba(12,187,29,0.06)",
                  borderTop: "1px solid #f0f0f0",
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    color: "#0cbb1d",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                  }}
                >
                  <i className="bi bi-patch-check-fill me-2" />
                  Este MVZ ya está verificado
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── VERIFICATION MODAL ─── */}
      {modal && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 2000,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              zIndex: 2010,
              background: "#fff",
              borderRadius: 12,
              padding: 32,
              width: 460,
              maxWidth: "95vw",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            <h5 style={{ margin: "0 0 20px", fontWeight: 700 }}>
              Verificar MVZ:{" "}
              <span style={{ color: "var(--fg-green)" }}>{modal.nombre}</span>
            </h5>

            {/* Select action if not chosen yet */}
            {!modal.accion && (
              <>
                <p
                  style={{
                    color: "var(--fg-muted)",
                    fontSize: "0.9rem",
                    marginBottom: 20,
                  }}
                >
                  ¿Qué decisión tomas sobre este Médico Veterinario Zootecnista?
                </p>
                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() =>
                      setModal((m) => (m ? { ...m, accion: "aprobar" } : m))
                    }
                    style={{
                      flex: 1,
                      padding: 14,
                      borderRadius: 8,
                      border: "2px solid var(--fg-green)",
                      background: "var(--fg-green-soft)",
                      color: "var(--fg-green)",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bi bi-check-circle-fill"
                      style={{
                        fontSize: "1.4rem",
                        display: "block",
                        marginBottom: 6,
                      }}
                    />
                    Aprobar
                  </button>
                  <button
                    onClick={() =>
                      setModal((m) => (m ? { ...m, accion: "rechazar" } : m))
                    }
                    style={{
                      flex: 1,
                      padding: 14,
                      borderRadius: 8,
                      border: "2px solid var(--fg-red)",
                      background: "rgba(214,13,76,0.06)",
                      color: "var(--fg-red)",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bi bi-x-circle-fill"
                      style={{
                        fontSize: "1.4rem",
                        display: "block",
                        marginBottom: 6,
                      }}
                    />
                    Rechazar
                  </button>
                </div>
              </>
            )}

            {/* Approve confirmation */}
            {modal.accion === "aprobar" && (
              <>
                <div
                  style={{
                    background: "rgba(12,187,29,0.08)",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 20,
                    color: "#07800f",
                  }}
                >
                  <i className="bi bi-info-circle me-2" />
                  Se enviará una notificación push FCM y un correo electrónico
                  al MVZ confirmando que su perfil fue <strong>aprobado</strong>
                  .
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setModal(null)}
                    disabled={modal.saving}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleVerificar}
                    disabled={modal.saving}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: "none",
                      background: "var(--fg-green)",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    {modal.saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Aprobando…
                      </>
                    ) : (
                      "✅ Confirmar aprobación"
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Reject flow */}
            {modal.accion === "rechazar" && (
              <>
                <div
                  style={{
                    background: "rgba(214,13,76,0.06)",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 20,
                    color: "var(--fg-red)",
                  }}
                >
                  <i className="bi bi-exclamation-triangle me-2" />
                  Debes indicar el <strong>motivo del rechazo</strong>. Se
                  enviará por push y email al médico.
                </div>
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: "0.88rem",
                    color: "var(--fg-text)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Motivo del rechazo *
                </label>
                <textarea
                  value={modal.motivo}
                  onChange={(e) =>
                    setModal((m) => (m ? { ...m, motivo: e.target.value } : m))
                  }
                  placeholder="Ej: La matrícula profesional no pudo ser verificada ante el COMVEZCOL. Por favor envía la resolución de grado oficial..."
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    resize: "vertical",
                    minHeight: 100,
                    fontSize: "0.88rem",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    marginTop: 16,
                  }}
                >
                  <button
                    onClick={() => setModal(null)}
                    disabled={modal.saving}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                      background: "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleVerificar}
                    disabled={modal.saving || !modal.motivo.trim()}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 8,
                      border: "none",
                      background: modal.motivo.trim()
                        ? "var(--fg-red)"
                        : "#ccc",
                      color: "#fff",
                      cursor: modal.motivo.trim() ? "pointer" : "default",
                      fontWeight: 700,
                    }}
                  >
                    {modal.saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Rechazando…
                      </>
                    ) : (
                      "❌ Confirmar rechazo"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ─── VISOR DE DOCUMENTO / IMAGEN ─── */}
      {docViewer && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setDocViewer(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.75)",
              zIndex: 3000,
            }}
          />

          {/* Panel */}
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              zIndex: 3010,
              background: "#fff",
              borderRadius: 12,
              width: "90vw",
              maxWidth: 900,
              height: "88vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
            }}
          >
            {/* Header del visor */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 20px",
                borderBottom: "1px solid #f0f0f0",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <i
                  className={`bi ${
                    detectDocType(docViewer.url) === "image"
                      ? "bi-image"
                      : detectDocType(docViewer.url) === "pdf"
                        ? "bi-file-earmark-pdf-fill"
                        : "bi-file-earmark-text"
                  }`}
                  style={{ color: "var(--fg-green)", fontSize: "1.1rem" }}
                />
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: "var(--fg-text)",
                  }}
                >
                  {docViewer.nombre}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Botón descargar (opcional) */}
                <a
                  href={docViewer.url}
                  download
                  title="Descargar archivo"
                  style={{
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                    background: "#fff",
                    color: "var(--fg-text)",
                    textDecoration: "none",
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <i className="bi bi-download" />
                  Descargar
                </a>
                {/* Botón cerrar */}
                <button
                  onClick={() => setDocViewer(null)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "1.3rem",
                    color: "var(--fg-muted)",
                    lineHeight: 1,
                    padding: "4px 6px",
                  }}
                  title="Cerrar"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            </div>

            {/* Cuerpo del visor */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                alignItems:
                  detectDocType(docViewer.url) === "image"
                    ? "center"
                    : "stretch",
                justifyContent:
                  detectDocType(docViewer.url) === "image"
                    ? "center"
                    : "stretch",
                background:
                  detectDocType(docViewer.url) === "image"
                    ? "#1a1a1a"
                    : "#f5f5f5",
                padding: detectDocType(docViewer.url) === "image" ? 16 : 0,
              }}
            >
              {detectDocType(docViewer.url) === "image" ? (
                // ── IMAGEN: render directo ────────────────────────────────────
                <img
                  src={docViewer.url}
                  alt={docViewer.nombre}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 4,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                  }}
                />
              ) : detectDocType(docViewer.url) === "pdf" ? (
                // ── PDF: iframe nativo del navegador ─────────────────────────
                <iframe
                  src={docViewer.url}
                  title={docViewer.nombre}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : (
                // ── Office / desconocido: Google Docs Viewer ──────────────────
                <iframe
                  src={buildViewerUrl(
                    docViewer.url,
                    detectDocType(docViewer.url),
                  )}
                  title={docViewer.nombre}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              )}
            </div>

            {/* Footer informativo */}
            <div
              style={{
                padding: "8px 20px",
                background: "#f8f9fa",
                borderTop: "1px solid #f0f0f0",
                fontSize: "0.74rem",
                color: "var(--fg-muted)",
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              {detectDocType(docViewer.url) === "image"
                ? "📷 Foto del documento de graduación"
                : detectDocType(docViewer.url) === "pdf"
                  ? "📄 Documento PDF — renderizado por el navegador"
                  : "📝 Documento — visualizado vía Google Docs Viewer"}
              {" · "}
              <span
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => window.open(docViewer.url, "_blank")}
              >
                Abrir en nueva pestaña
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
